﻿import React, { Fragment, useState, useEffect } from 'react';
import { Intent } from '@blueprintjs/core';

import { NotificationInline, NotificationToaster } from 'components/notifications';

import { FormDateRangeInput, FormRadio, FormSelect, FormTextInput, FormSuggest, FormNumericInput, FormDateInput, FormLocationSelector, FormMultiSelect } from 'components/form-fields';
import { useValidation } from "hooks/useValidation";
import { ShowHide } from 'components/layout';
import { EmployeeService } from "services";

import PropTypes from 'prop-types';
import moment from "moment";
import 'components/settings/settings.css';

const defaultArray = [];

export function ReportParameters(
    {
        reportId = 0,
        disabled = false,
        loading = false,
        locations = defaultArray,
        eventTypes = [],
        restrictDateRange = false,
        restrictNumberOfMonths = null,
        filterRequired = true,
        fixedStartDate = null,
        fixedEndDate = null,
        editing = false,
        fileTypes,
        onFormatUpdate,
        onParamUpdate,
        onValidationUpdate,
        parameters,
        values
    }
) {

    const [isValid, errors, validate] = useValidation();
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [filterType, setFilterType] = useState(null);
    const [driverList, setDriverList] = useState([]);
    const [validationRules, setValidationRules] = useState([]);
    const [locationsValue, setLocations] = useState([]);
    const [showLocationsAsHierarachy, setShowLocationsAsHierarchy] = useState(true);
    const numWithBounds = 'number with bounds';
    const intMinDefault = 0;
    const intMaxDefault = 99999999;
    const eventTypeParameterNames = ["Event_Type_List", "Event_Type"];
    const multiSelectParameterNames = [...eventTypeParameterNames];

    useEffect(function () {

        if (parameters == null) {
            return;
        }

        var rules = [
            { fieldName: "reportType", required: true }
        ];

        parameters.forEach(function (rp) {
            if (!rp.hidden && !rp.optional && rp.parameterType.key !== "LocationList" && rp.parameterType.key !== "DriverList") {
                rules.push({ fieldName: rp.name, required: true })
            }
        });

        setValidationRules(rules);

    }, [parameters]);

    useEffect(function () {
        setLocations(locations);
    }, [locations]);

    useEffect(function () {
        setSelectedLocations([]);
        setSelectedDriver(null);
        setDriverList([]);
        setFilterType(null);
    }, [reportId]);

    function setUseHierarchy() {
        setShowLocationsAsHierarchy(previous => !previous);
    }

    function handleMultiSelectItemSelect(item, parameter, selectedValues) {
        let newSelected = [...selectedValues];
        if (newSelected.some(val => val.id === item.id)) {
            newSelected = newSelected.filter(val => val.id !== item.id);
        } else {
            newSelected.push(item);
        }
        setGenerationParam(parameter.name, newSelected.map(val => val.id).join(","));
    }

    function handleMultiSelectItemRemove(index, parameter, selectedValues) {
        let newSelected = [...selectedValues];
        newSelected.splice(index, 1);
        setGenerationParam(parameter.name, newSelected.map(val => val.id).join(","));
    }

    function getItemsProp(parameterName) {
        if (!multiSelectParameterNames.includes(parameterName)) return [];
        if (eventTypeParameterNames.includes(parameterName)) {
            return eventTypes;
        }
        return [];
    }

    function printFormInput(parameter) {

        if (parameter.hidden) {
            return (<Fragment key={parameter.id}></Fragment>);
        }

        if (multiSelectParameterNames.includes(parameter.name)) {
            const itemsProp = getItemsProp(parameter.name);
            if (itemsProp && itemsProp.length > 0) {
                const selectedIds = new Set((getParamValue(parameter.name) || "").split(",").filter(Boolean).map(Number));
                const selectedValues = itemsProp.filter(val => selectedIds.has(val.id));
                return (
                    <div key={parameter.id}>
                        <FormMultiSelect
                            headingText={parameter.friendlyName}
                            items={itemsProp}
                            selectedValues={selectedValues}
                            onItemSelect={item => handleMultiSelectItemSelect(item, parameter, selectedValues)}
                            onRemove={(_item, index) => handleMultiSelectItemRemove(index, parameter, selectedValues)}
                            loading={loading}
                            disabled={disabled}
                            dangerHelperText={errors[parameter.name]}
                        />
                    </div>
                );
            }
        }

        switch (parameter.parameterType.key) {
            case "String":
                if (parameter.options && parameter.options.length > 0) {
                    let filteredOptions = parameter.options;
                    let showDropdown = true;

                    if (parameter.parentReportParameterId) {
                        const parentParam = parameters.find(
                            p => String(p.id) === String(parameter.parentReportParameterId)
                        );
                        if (parentParam) {
                            const parentSelectedValue = values.parameters.find(
                                p => p.Key === parentParam.name
                            )?.Value;
                            const parentSelectedOption = parentParam.options?.find(
                                o => o.value === parentSelectedValue
                            );
                            const parentSelectedOptionId = parentSelectedOption?.id ?? null;

                            // Filter: show options with no parent, or matching parent
                            filteredOptions = parameter.options.filter(
                                o => o.parentOptionId === null || o.parentOptionId === parentSelectedOptionId
                            );

                            // Only show if there is at least one child option for this parent
                            showDropdown = parameter.options.some(
                                o => o.parentOptionId === parentSelectedOptionId
                            );
                        } else {
                            filteredOptions = [];
                            showDropdown = false;
                        }
                    }

                    if (showDropdown && filteredOptions.length > 0) {
                        return (
                            <div key={parameter.id}>
                                <FormSelect
                                    headingText={`${parameter.friendlyName}:`}
                                    disabled={disabled}
                                    loading={loading}
                                    dangerHelperText={errors[parameter.name]}
                                    onItemSelect={item => { onSelectChange(item, parameter.name); }}
                                    selectedValue={getParamValue(parameter.name)}
                                    items={filteredOptions.map(o => ({
                                        id: o.value,
                                        name: o.name
                                    }))}
                                />
                            </div>
                        );
                    } else {
                        return <Fragment key={parameter.id}></Fragment>;
                    }
                } else {
                    return (
                        <div key={parameter.id}>
                            <FormTextInput
                                headingText={`${parameter.friendlyName}:`}
                                disabled={disabled}
                                dangerHelperText={errors[parameter.name]}
                                loading={loading}
                                onChange={item => { onTextChange(item, parameter.name); }}
                                value={getParamValue(parameter.name)}
                            />
                        </div>
                    );
                }
            case "Integer":
            case "Decimal":
                return (
                    <div key={parameter.id}>
                        <FormNumericInput headingText={`${parameter.friendlyName}:`} disabled={disabled} dangerHelperText={errors[parameter.name]} loading={loading} allowDecimal={parameter.parameterType.key === "Decimal"} onValueChange={(item) => { onNumericChange(item, parameter.name); }} min={parameter.intMin == null ? intMinDefault : parameter.intMin} max={parameter.intMax == null ? intMaxDefault : parameter.intMax} selectedValue={getParamValue(parameter.name)} />
                    </div>
                );
            case "Boolean":
                return (
                    <div key={parameter.id}>
                        <FormRadio headingText={`${parameter.friendlyName}:`} disabled={disabled} dangerHelperText={errors[parameter.name]} loading={loading} onChange={(item) => { onRadioChange(item, parameter.name); }} selectedValue={getParamValue(parameter.name)} options={[{ value: 'true', label: "Yes" }, { value: 'false', label: "No" }]} />
                    </div>
                );
            case "StartDate":
                return (
                    <div key={parameter.id}>
                        <FormDateRangeInput headingText={`Date range:`} helperText={restrictDateRange && restrictNumberOfMonths != null ? `Restricted to a maximum of ${restrictNumberOfMonths} months` : null} disabled={disabled || fixedStartDate || fixedEndDate || editing} dangerHelperText={errors[parameter.name]} loading={loading} onChange={onDateRangeChange} startDate={getParamValueByType('StartDate')} endDate={getParamValueByType('EndDate')} maxDate={calculateMaxDateRangeDate()} initialMonthOffset={-1} />
                        <NotificationInline allowClose={false} text={"The date range has been locked because you have selected for your reports to run on a 4-4-5 schedule. Dates will be moved on automatically each time the schedule runs."} intent="info" show={fixedStartDate || fixedEndDate} />
                    </div>
                );
            case "Date":
                return (
                    <div key={parameter.id}>
                        <FormDateInput headingText={`${parameter.friendlyName}:`} disabled={disabled || editing} dangerHelperText={errors[parameter.name]} loading={loading} onChange={(item) => { onDateChange(item, parameter.name); }} value={getParamValue(parameter.name)} />
                    </div>
                );
            case "EndDate":
            case "DriverList":
                return (
                    <Fragment key={parameter.id}></Fragment>
                );
            case "LocationList":

                var hasDriverSelect = getParamByType('DriverList') != null;

                if (hasDriverSelect) {
                    return (
                        <div key={parameter.id}>
                            <FormRadio headingText="How would you like to filter your report?" disabled={disabled} dangerHelperText={errors.filterType} loading={loading} onChange={(item) => { onFilterSelect(item); }} selectedValue={filterType} options={[{ value: 'location', label: "By location" }, { value: 'driver', label: "By driver" }]} />
                            <ShowHide
                                evaluator={filterType === "location"}
                                show={(
                                    <FormLocationSelector
                                        headingText={`Select locations:`}
                                        dangerHelperText={errors[parameter.name]}
                                        businessArea={""}
                                        loading={loading || locationsValue.length === 0}
                                        selectedLocations={selectedLocations}
                                        setSelectedLocations={onLocationSelect}
                                        useHierarchy={showLocationsAsHierarachy}
                                        setUseHierarchy={setUseHierarchy}
                                        useLocationDefaults={false}
                                        useCustomLocations
                                        customLocations={locationsValue}
                                        locationDefaultsToTrue
                                        disabled={disabled}
                                    />
                                )}
                            />
                            <ShowHide
                                evaluator={filterType === "driver"}
                                show={(
                                    <FormSuggest loading={loading} disabled={disabled} items={driverList} onItemSelect={onDriverSelect} dangerHelperText={errors.DriverID} placeholder="Select driver" headingText="Select driver:" selectedValue={selectedDriver} onFilter={onDriverSearch} debounceTime={500} />
                                )}
                            />
                        </div>
                    );
                } else {
                    return (
                        <div key={parameter.id}>
                            <FormLocationSelector
                                headingText={`Select locations:`}
                                dangerHelperText={errors[parameter.name]}
                                businessArea={""}
                                loading={loading || locationsValue.length === 0}
                                selectedLocations={selectedLocations}
                                setSelectedLocations={onLocationSelect}
                                useHierarchy={showLocationsAsHierarachy}
                                setUseHierarchy={setUseHierarchy}
                                useLocationDefaults={false}
                                useCustomLocations
                                customLocations={locationsValue}
                                locationDefaultsToTrue
                                disabled={disabled}
                            />
                        </div>
                    );
                }

            default:
                return (
                    <div key={parameter.id}>
                        <p>{parameter.friendlyName}</p>
                    </div>
                );
        }
    }

    function onFormatChange(item) {
        var reportTypeId = parseInt(item.currentTarget.value);
        onFormatUpdate(reportTypeId);
    }

    function onRadioChange(item, paramName) {
        setGenerationParam(paramName, item.currentTarget.value);
    }

    function onNumericChange(item, paramName) {
        if (item != null) {
            setGenerationParam(paramName, item);
        }
    }

    function onSelectChange(item, paramName) {
        setGenerationParam(paramName, item.id);
    }

    function onDateRangeChange(item) {
        setGenerationParamByType('StartDate', item.startDate.date != null ? item.startDate.dateString : null);
        setGenerationParamByType('EndDate', item.endDate.dateString != null ? item.endDate.dateString : null);
    }

    useEffect(function () {

        if (fixedStartDate != null && fixedEndDate != null) {
            setGenerationParamByType('StartDate', fixedStartDate);
            setGenerationParamByType('EndDate', fixedEndDate);
        }
    }, [fixedStartDate, fixedEndDate, values.parameters.length])

    function onDateChange(item, paramName) {
        setGenerationParam(paramName, item ? moment(item).format("YYYY-MM-DD") : null);
    }

    function onTextChange(item, paramName) {
        setGenerationParam(paramName, item.target.value);
    }

    function getParamValue(paramName) {
        var foundParam = values.parameters.find(function (p) {
            return p.Key === paramName;
        });

        return foundParam == null ? null : foundParam.Value
    }

    function getParamValueByType(typeName) {

        var foundParam = values.parameters.find(function (p) {
            return p.Type === typeName;
        });

        return foundParam == null ? null : foundParam.Value
    }

    function getParamByType(typeName) {
        return values.parameters.find(function (p) {
            return p.Type === typeName;
        });
    }

    function setGenerationParam(paramName, value) {

        var clonedParams = [
            ...values.parameters
        ];

        var changingParam = clonedParams.find(function (p) {
            return p.Key === paramName;
        });

        if (changingParam == null) {
            return;
        }

        changingParam.Value = value;

        onParamUpdate(clonedParams);
    }

    function setGenerationParamByType(paramType, value) {

        var clonedParams = [
            ...values.parameters
        ];


        var changingParam = clonedParams.find(function (p) {
            return p.Type === paramType;
        });

        if (changingParam == null) {
            return;
        }

        changingParam.Value = value;

        onParamUpdate(clonedParams);

    }

    function calculateMaxDateRangeDate() {

        if (restrictDateRange && restrictNumberOfMonths != null) {
            var startParam = values.parameters.find(function (p) {
                return p.Type === "StartDate";
            });

            if (startParam != null && startParam.Value != null && startParam.Value !== "") {
                return moment(startParam.Value).add(restrictNumberOfMonths, 'months').locale("en-gb").toDate();
            }
        }

        var maxMonths = 12;
        return moment().add(maxMonths, 'months').locale("en-gb").toDate();
    }

    function onFilterSelect(item) {
        setFilterType(item.currentTarget.value);

        if (item.currentTarget.value === "location") {
            setSelectedDriver(null)
        } else {
            setSelectedLocations([]);
        }

    }

    function onLocationSelect(allSelectedlocations) {
        setSelectedLocations([
            ...allSelectedlocations
        ]);
        setGenerationParamByType('LocationList', allSelectedlocations.join(","));
    }

    function onDriverSearch(query) {

        var driversToShow = 50;

        EmployeeService.getEmployeeList(driversToShow, 1, query, "FIRSTNAME", "A", [{ name: "status", value: "active" }, { name: "status", value: "away" }]).then(function (driverResponse) {
            setDriverList(driverResponse.data.map(function (d) {
                return {
                    id: d.employeeId,
                    name: `${d.fullName} - ${d.location.name}`
                }
            }));
        }, function () {
            NotificationToaster.show(Intent.WARNING, "Could not search for a specific driver, please try agian.");
            setDriverList([]);
        });
    }

    function onDriverSelect(item) {
        setSelectedDriver(item.id);
    }

    useEffect(function () {
        setGenerationParamByType('DriverList', selectedDriver);
    }, [selectedDriver]);


    function validateFields() {

        var requestValidation = {
            reportType: values.reportType,
            filterType: filterType
        };

        values.parameters.forEach(param => {
            requestValidation[param.Key] = param.Value;
        });

        var rules = [
            ...validationRules
        ];

        //Check for location and driver fields and if present add that to validation

        if (filterRequired) {
            var locationParam = getParamByType("LocationList");
            var driverParam = getParamByType("DriverList");

            if (locationParam != null && driverParam != null) {
                rules.push({ fieldName: "filterType", required: true });

                if (filterType === "location") {
                    rules.push({ fieldName: locationParam.Key, required: true });
                }

                if (filterType === "driver") {
                    rules.push({ fieldName: driverParam.Key, required: true });
                }

            } else if (locationParam != null) {
                rules.push({ fieldName: locationParam.Key, required: true })
            }
        }

        let paramsWithBounds = null;

        if (parameters && parameters.length > 0) {
            paramsWithBounds = parameters.filter(p => p.parameterType.name.toLowerCase() === numWithBounds);
        }

        if (paramsWithBounds && paramsWithBounds.length > 0) {
            paramsWithBounds.forEach(p => {
                rules.push({ fieldName: p.name, minValue: p.intMin, maxValue: p.intMax })
            });
        }

        validate(rules, requestValidation);
    }

    useEffect(validateFields, [values.parameters, filterType, values.reportType, validationRules]);

    useEffect(function () {
        onValidationUpdate(isValid);
    }, [isValid]);

    useEffect(() => {
        if (!parameters || !values || !values.parameters) return;

        parameters.forEach(parameter => {
            if (parameter.parentReportParameterId) {
                const paramValue = values.parameters.find(p => p.Key === parameter.name)?.Value;
                if (paramValue == null) {
                    const parentParam = parameters.find(
                        p => String(p.id) === String(parameter.parentReportParameterId)
                    );
                    if (!parentParam) return;

                    const parentSelectedValue = values.parameters.find(
                        p => p.Key === parentParam.name
                    )?.Value;
                    const parentSelectedOption = parentParam.options?.find(
                        o => o.value === parentSelectedValue
                    );
                    const parentSelectedOptionId = parentSelectedOption?.id ?? null;

                    // Only set default if parent is selected
                    if (parentSelectedValue) {
                        const filteredOptions = parameter.options?.filter(
                            o => o.parentOptionId === null || o.parentOptionId === parentSelectedOptionId
                        ) || [];
                        const defaultOption = filteredOptions.find(o => o.default) || filteredOptions[0];
                        if (defaultOption) {
                            setGenerationParam(parameter.name, defaultOption.value);
                        }
                    }
                }
            }
        });
    }, [parameters, values.parameters]);

    return (
        <Fragment>
            {parameters && parameters.map(function (p) {
                return printFormInput(p)
            })}
            <FormRadio
                disabled={disabled}
                onChange={onFormatChange}
                headingText="Report format:"
                selectedValue={values.reportType}
                loading={loading}
                options={fileTypes}
                id="generate-report-format"
                dangerHelperText={errors.reportType}
            ></FormRadio>
        </Fragment>
    );
}

ReportParameters.propTypes = {
    reportId: PropTypes.number,
    parameters: PropTypes.array.isRequired,
    fileTypes: PropTypes.array.isRequired,
    values: PropTypes.object.isRequired,
    onParamUpdate: PropTypes.func.isRequired,
    onFormatUpdate: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    locations: PropTypes.array,
    eventTypes: PropTypes.array,
    restrictDateRange: PropTypes.bool,
    restrictNumberOfMonths: PropTypes.number,
    onValidationUpdate: PropTypes.func.isRequired,
    filterRequired: PropTypes.bool,
    fixedStartDate: PropTypes.string,
    fixedEndDate: PropTypes.string,
    editing: PropTypes.bool
};